/* eslint-disable react-hooks/rules-of-hooks */
import React, { useCallback, useMemo, useState } from 'react';
import {
  createTableColumn,
  TableFeaturePlugin,
  TableRowId,
  TableSelectionCell,
  useTableFeatures,
  useTableSelection,
} from '@fluentui/react-components';
import { ITableColumnSizingState, TSelectableTableRow, UseTableColumnSizingParams } from '../types/hooks.types';
import { usePersistedColumnSizing } from './usePersistedColumnSizing.hook';

type TableOptions = {
  shouldHighlightSelected?: boolean;
  /**
   * Turn on column resizing and supply sizing options
   */
  resizable?: boolean;
  tableId?: string; // used for persisted column sizing
  columnSizingParams?: UseTableColumnSizingParams;
};

const defaultTableOptions: TableOptions = {
  shouldHighlightSelected: true,
  resizable: false,
};

/**
 * Re-usable hook that adds multiselect + “first-row shows actions” UX on top of
 * {@link useTableFeatures}. Returned `tableRows` are already enriched with
 * selection cells, hover state and an optional context-menu contract.
 */
export const useSelectableTable = <T,>(
  columnIds: string[],
  items: T[],
  getRowId: (item: T) => string,
  selectedItemIds: Set<string>, // we use string instead of Fluent-UI's TableRowId for simplification
  setSelectedItemIds: (selectedItemIds: Set<string>) => void,
  { shouldHighlightSelected, resizable, tableId, columnSizingParams }: TableOptions = defaultTableOptions
) => {
  /* -------- column + selection boilerplate -------------------------------- */
  const columns = useMemo(() => columnIds.map((columnId) => createTableColumn<T>({ columnId })), [columnIds]);

  // only include the resizing plugin when requested
  const plugins: TableFeaturePlugin[] = [
    useTableSelection({
      selectionMode: 'multiselect',
      selectedItems: selectedItemIds,
      onSelectionChange: (_, d) => setSelectedItemIds(d.selectedItems as Set<string>),
    }),
  ];

  if (resizable && tableId && columnSizingParams) {
    plugins.push(usePersistedColumnSizing(tableId, columnSizingParams));
  }

  const {
    columnSizing_unstable,
    getRows,
    selection: { allRowsSelected, someRowsSelected, toggleAllRows, toggleRow, isRowSelected },
  } = useTableFeatures({ columns, items, getRowId }, plugins);

  /* Map raw rows to fluent-table props */
  const rows = getRows((row) => {
    const selected = isRowSelected(row.rowId);

    return {
      ...row,
      onClick: (e: React.MouseEvent) => toggleRow(e, row.rowId),
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          toggleRow(e, row.rowId);
        }
      },
      selected,
      appearance: shouldHighlightSelected && selected ? ('brand' as const) : ('none' as const),
    };
  });

  /* key-down handler for the header checkbox */
  const toggleAllKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        toggleAllRows(e);
      }
    },
    [toggleAllRows]
  );

  const selectionHeaderCell = (
    <TableSelectionCell
      checked={allRowsSelected ? true : someRowsSelected ? 'mixed' : false}
      onClick={toggleAllRows}
      onKeyDown={toggleAllKeydown}
      checkboxIndicator={{ 'aria-label': 'Select all rows' }}
      style={{ width: 33 }}
    />
  );

  /* -------- UX helpers ---------------------------------------------------- */
  const [hoveredRow, setHoveredRow] = useState<TableRowId | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<TableRowId | null>(null);

  const firstRowId = rows[0]?.rowId;

  const tableRows: TSelectableTableRow<T>[] = rows.map((row) => {
    /**
     * Action pills are visible when …
     *  A) it’s the *first* row and nothing else is hovered/open
     *  B) its own context menu is open
     */
    const alwaysVisible =
      (row.rowId === firstRowId && hoveredRow === null && menuOpenId === null) || menuOpenId === row.rowId;

    return {
      /* props expected by <TableRow> */
      ...row,
      'aria-selected': row.selected,

      /* selection cell */
      selectionCell: (
        <TableSelectionCell subtle checked={row.selected} checkboxIndicator={{ 'aria-label': 'Select row' }} />
      ),
      key: row.rowId as string,
      preventSelect: (e: React.MouseEvent<any>) => e.stopPropagation(), // prevent row selection

      /* highlight first-row pill, hover pill background otherwise */
      alwaysVisible,
      // actionButtonClassName: alwaysVisible ? '!bg-neutralBg-3' : '!bg-neutralBg-1',

      /* hover tracking (for first-row pill logic) */
      onMouseEnter: () => setHoveredRow(row.rowId),
      onMouseLeave: () => setHoveredRow(null),

      /* contract for an optional context-menu */
      menu: {
        isOpen: menuOpenId === row.rowId,
        onOpenChange: (_, d) => setMenuOpenId(d.open ? row.rowId : null),
        onContextMenu: (e) => {
          e.preventDefault(); // suppress browser menu
          setMenuOpenId(row.rowId);
        },
      },
    };
  });

  return { selectionHeaderCell, tableRows, columnSizing: columnSizing_unstable as undefined | ITableColumnSizingState };
};
